// voice.ts
/**
 * voice.js — Akili Brain: Voice Recording & Transcription
 * Handles mic access, MediaRecorder lifecycle, audio visualiser, and
 * Whisper transcription via /api/ai/transcribe.
 *
 * Phase E addition: exports playWithLipSync() for modules that receive
 * TTS audio and want to drive Kili's jaw via the avatar-3d lip-sync system.
 * Call it from whichever module handles Kili's TTS audio response, e.g.:
 *
 *   import { playWithLipSync } from './voice.js';
 *   // When TTS ArrayBuffer arrives from the AI response:
 *   await playWithLipSync(ttsAudioArrayBuffer);
 */

import { state } from './state';
import { transcribe } from './api';
import { appendTypingIndicator, removeTypingIndicator, appendMessage, showToast } from './ui-renderers';

// Module-level timer handle so we can cancel the auto-stop on manual stop.
let autoStopTimer = null;

// ── Audio Visualiser ──────────────────────────────────────────────────────────

function startAudioVisualiser(stream) {
  try {
    if (!state.audioCtx) {
      state.audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    }
    if (state.audioCtx.state === 'suspended') {
      state.audioCtx.resume();
    }
    state.audioAnalyser = state.audioCtx.createAnalyser();
    state.audioAnalyser.fftSize = 128;
    const source = state.audioCtx.createMediaStreamSource(stream);
    source.connect(state.audioAnalyser);

    const micBtn = document.getElementById('ai-mic-btn');
    if (!micBtn) return;
    let viz = micBtn.querySelector('.ai-visualiser-canvas') as HTMLCanvasElement | null;
    if (!viz) {
      viz = document.createElement('canvas') as HTMLCanvasElement;
      viz.className = 'ai-visualiser-canvas';
      viz.width = 34; viz.height = 34;
      micBtn.appendChild(viz);
    }
    const ctx       = viz.getContext('2d');
    const dataArray = new Uint8Array(state.audioAnalyser.frequencyBinCount);

    function drawViz() {
      if (!state.isRecording) {
        if (viz.parentNode) viz.parentNode.removeChild(viz);
        return;
      }
      state.audioAnalyser.getByteFrequencyData(dataArray);
      ctx.clearRect(0, 0, 34, 34);
      let avg = 0;
      for (let i = 0; i < dataArray.length; i++) avg += dataArray[i];
      avg = avg / dataArray.length;
      const radius = 8 + (avg / 255) * 12;
      ctx.beginPath();
      ctx.arc(17, 17, radius, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(0,229,255,' + (0.25 + (avg / 255) * 0.55) + ')';
      ctx.fill();
      state.audioVizRafId = requestAnimationFrame(drawViz);
    }
    drawViz();
  } catch (_) {
    // Non-critical — visualiser degrades silently
  }
}

function stopAudioVisualiser() {
  if (state.audioVizRafId) {
    cancelAnimationFrame(state.audioVizRafId);
    state.audioVizRafId = null;
  }
  const micBtn = document.getElementById('ai-mic-btn');
  if (micBtn) {
    const viz = micBtn.querySelector('.ai-visualiser-canvas');
    if (viz && viz.parentNode) viz.parentNode.removeChild(viz);
  }
}

// ── Mic button state ──────────────────────────────────────────────────────────

function updateMicBtn(recording) {
  const btn = document.getElementById('ai-mic-btn');
  if (!btn) return;
  if (recording) {
    btn.classList.add('recording');
    btn.setAttribute('title', 'Stop recording');
  } else {
    btn.classList.remove('recording');
    btn.setAttribute('title', 'Record voice message');
  }
}

// ── Recording ─────────────────────────────────────────────────────────────────

export function startRecording() {
  if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
    showVoiceError('Voice input is not supported in this browser.');
    return;
  }

  navigator.mediaDevices.getUserMedia({ audio: true })
    .then(stream => {
      state.audioChunks = [];
      state.isRecording = true;

      let mimeType = '';
      if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
        mimeType = 'audio/webm;codecs=opus';
      } else if (MediaRecorder.isTypeSupported('audio/webm')) {
        mimeType = 'audio/webm';
      } else if (MediaRecorder.isTypeSupported('audio/mp4')) {
        mimeType = 'audio/mp4';
      }

      state.mediaRecorder = new MediaRecorder(stream, mimeType ? { mimeType } : {});

      state.mediaRecorder.addEventListener('dataavailable', e => {
        if (e.data && e.data.size > 0) state.audioChunks.push(e.data);
      });

      state.mediaRecorder.addEventListener('stop', () => {
        const mType = state.mediaRecorder.mimeType || 'audio/webm';
        const blob  = new Blob(state.audioChunks, { type: mType });
        transcribeBlob(blob, mType);
        stream.getTracks().forEach(t => t.stop());
        stopAudioVisualiser();
      });

      state.mediaRecorder.start();
      updateMicBtn(true);
      startAudioVisualiser(stream);
      // Auto-stop after 60 s; store the ID so stopRecording() can cancel it.
      autoStopTimer = setTimeout(() => { if (state.isRecording) stopRecording(); }, 60000);
    })
    .catch(e => {
      console.warn('[AkiliBrain] Mic access denied:', e);
      showVoiceError('Microphone access was denied. Please allow mic access in your browser.');
    });
}

export function stopRecording() {
  // Cancel the 60-second auto-stop timer if the user stops manually.
  if (autoStopTimer) {
    clearTimeout(autoStopTimer);
    autoStopTimer = null;
  }
  if (state.mediaRecorder && state.isRecording) state.mediaRecorder.stop();
  state.isRecording = false;
  updateMicBtn(false);
  stopAudioVisualiser();
}

// ── Transcription ─────────────────────────────────────────────────────────────

async function transcribeBlob(blob, mimeType) {
  const micBtn = document.getElementById('ai-mic-btn') as HTMLButtonElement | null;
  if (micBtn) micBtn.disabled = true;
  appendTypingIndicator();

  try {
    const data = await transcribe(blob, mimeType);
    removeTypingIndicator();

    const text = (data.text || '').trim();

    if (!text) {
      showVoiceError('No speech detected. Please try speaking more clearly.');
      return;
    }

    // ── Kili Sentient: classify voice intent ──────────────────────────────────
    let finalText = text;

    try {
      const intentRes = await fetch('/api/ai/voice-intent', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ transcript: text }),
      });

      if (intentRes.ok) {
        const { intent, confidence, extractedData } = await intentRes.json();

        if (confidence !== 'low' && intent !== 'general_query') {
          const { showVoiceIntentBadge } = await import('./ui-renderers.js');
          showVoiceIntentBadge(intent, extractedData);

          const INTENT_PREFIX: Record<string, string> = {
            attendance_note: 'Create attendance note: ',
            dictate_letter:  'Draft a letter: ',
            add_court_date:  'Add court date: ',
            add_task:        'Add task: ',
            dictate_memo:    'Draft internal memo: ',
          };
          finalText = (INTENT_PREFIX[intent] ?? '') + text;
        }
      }
    } catch (e) {
      console.debug('[voice-intent] Classification failed, using raw transcript:', e);
    }
    // ── end intent classification ─────────────────────────────────────────────

    const input = document.getElementById('ai-input');
    if (input) {
      (input as HTMLInputElement).value = finalText;
      document.dispatchEvent(new CustomEvent('akili:voice-transcribed', { detail: finalText }));
    }
  } catch (e) {
    removeTypingIndicator();
    console.error('[AkiliBrain] Transcription error:', e);
    showVoiceError(e.message || 'Voice input failed. Please type your message instead.');
  } finally {
    if (micBtn) micBtn.disabled = false;
    state.audioChunks = [];
  }
}

// ── Voice error ───────────────────────────────────────────────────────────────

export function showVoiceError(msg) {
  removeTypingIndicator();
  appendMessage('bot', '\u26A0\uFE0F ' + msg);
  showToast(msg, 'error');
}

// ── Phase E: Lip-sync bridge ──────────────────────────────────────────────────
// Convenience wrapper so any module that receives Kili's TTS audio can trigger
// lip-sync with a single import from voice.ts — the most natural home for
// anything audio-related in the Akili module graph.
//
// Usage (e.g. in your TTS handler, handlers.ts, or wherever TTS audio lands):
//
//   import { playWithLipSync } from './voice.js';
//   // ttsBuffer is the ArrayBuffer returned by your TTS API call
//   await playWithLipSync(ttsBuffer);

export async function playWithLipSync(ttsBuffer: ArrayBuffer): Promise<void> {
  try {
    // Dynamic import keeps the avatar-3d bundle out of the voice.ts parse path
    // for users who are in text-only mode and never load the 3D avatar.
    const { startLipSync } = await import('./avatar-3d.js');
    await startLipSync(ttsBuffer);
  } catch (err) {
    // avatar-3d module may not be loaded yet (drawer closed) — degrade silently.
    // The TTS audio was already passed to startLipSync which handles its own
    // AudioContext, so audio plays regardless.
    console.debug('[voice] playWithLipSync: avatar-3d not ready, playing without lip sync', err);
  }
}
